import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrderStore } from "@/store/orderStore";
import { formatDistanceToNow } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Bell, CheckCircle, HelpCircle, CreditCard } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import { ActivityLog } from "@/types/orders";
import { useActivityTranslation } from "@/hooks/useActivityTranslation";

interface ActivityLogTableProps {
  storeId: string;
  entityType?: string;
  limit?: number;
}

export const ActivityLogTable = ({
  storeId,
  entityType,
  limit = 50,
}: ActivityLogTableProps) => {
  const { t } = useTranslation();
  const { getActionTranslation } = useActivityTranslation();
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const { markActivityViewed } = useOrderStore();

  useEffect(() => {
    const fetchActivities = async () => {
      if (!storeId) return;

      setLoading(true);
      try {
        let query = supabase
          .from("activity_logs")
          .select("*")
          .eq("store_id", storeId)
          .order("created_at", { ascending: false })
          .limit(limit);

        if (entityType) {
          query = query.eq("entity_type", entityType);
        }

        const { data, error } = await query;

        if (error) throw error;
        setActivities(data as ActivityLog[]);
      } catch (error) {
        console.error("Error fetching activity logs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();

    // Set up real-time subscription
    const subscription = supabase
      .channel("activity-logs-changes")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "activity_logs",
          filter: `store_id=eq.${storeId}${
            entityType ? `&entity_type=eq.${entityType}` : ""
          }`,
        },
        (payload) => {
          setActivities((current) => [payload.new as ActivityLog, ...current]);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [storeId, entityType, limit]);

  const handleRowClick = async (activity: ActivityLog) => {
    // Mark the activity as viewed based on details or custom field
    const isUnviewed = activity.details && !activity.details.viewed;

    if (isUnviewed) {
      try {
        await markActivityViewed(activity.id);
        // Update local state
        setActivities((prev) =>
          prev.map((a) =>
            a.id === activity.id
              ? { ...a, details: { ...a.details, viewed: true } }
              : a
          )
        );
      } catch (error) {
        console.error("Error marking activity as viewed:", error);
      }
    }
  };

  const getActionIcon = (entityType: string) => {
    switch (entityType) {
      case "order_reminder":
        return <Bell className="h-4 w-4 text-amber-500" />;
      case "payment_request":
        return <CreditCard className="h-4 w-4 text-green-500" />;
      case "assistance_request":
        return <HelpCircle className="h-4 w-4 text-blue-500" />;
      default:
        return <CheckCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  // Determine if an activity is viewed based on its details
  const isActivityViewed = (activity: ActivityLog) => {
    return activity.details && activity.details.viewed === true;
  };

  // Format the action text for display
  const formatActionText = (activity: ActivityLog) => {
    if (activity.entity_type === "order_reminder") {
      const tableInfo = activity.details?.table_number
        ? ` (${t("table")} ${activity.details.table_number})`
        : "";
      return `${getActionTranslation("order_reminder")}${tableInfo}`;
    } else if (activity.entity_type === "payment_request") {
      const tableInfo = activity.details?.table_number
        ? ` (${t("table")} ${activity.details.table_number})`
        : "";
      return `${getActionTranslation("payment_request")}${tableInfo}`;
    } else if (activity.entity_type === "assistance_request") {
      const tableInfo = activity.details?.table_number
        ? ` (${t("table")} ${activity.details.table_number})`
        : "";
      return `${getActionTranslation("assistance_request")}${tableInfo}`;
    }

    return getActionTranslation(activity.action);
  };

  if (loading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-muted-foreground">{t("noActivityRecorded")}</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("action")}</TableHead>
            <TableHead>{t("user")}</TableHead>
            <TableHead className="hidden md:table-cell">{t("type")}</TableHead>
            <TableHead className="text-right">{t("time")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {activities.map((activity) => (
            <TableRow
              key={activity.id}
              onClick={() => handleRowClick(activity)}
              className={`${
                !isActivityViewed(activity) &&
                [
                  "order_reminder",
                  "payment_request",
                  "assistance_request",
                ].includes(activity.entity_type)
                  ? "bg-muted/50 cursor-pointer"
                  : ""
              }`}
            >
              <TableCell className="font-medium">
                <div className="flex items-center gap-2">
                  {getActionIcon(activity.entity_type)}
                  <span>
                    {formatActionText(activity)}
                    {!isActivityViewed(activity) &&
                      [
                        "order_reminder",
                        "payment_request",
                        "assistance_request",
                      ].includes(activity.entity_type) && (
                        <span className="ml-2 inline-block h-2 w-2 rounded-full bg-red-500"></span>
                      )}
                  </span>
                </div>
              </TableCell>
              <TableCell>{activity.user_name}</TableCell>
              <TableCell className="hidden md:table-cell">
                <Badge
                  variant="outline"
                  className={`${
                    activity.entity_type === "order_reminder"
                      ? "border-amber-500 text-amber-500"
                      : activity.entity_type === "payment_request"
                      ? "border-green-500 text-green-500"
                      : activity.entity_type === "assistance_request"
                      ? "border-blue-500 text-blue-500"
                      : activity.entity_type === "order"
                      ? "border-purple-500 text-purple-500"
                      : activity.entity_type === "menu_item"
                      ? "border-orange-500 text-orange-500"
                      : activity.entity_type === "store"
                      ? "border-indigo-500 text-indigo-500"
                      : activity.entity_type === "attendance"
                      ? "border-cyan-500 text-cyan-500"
                      : activity.entity_type === "table"
                      ? "border-pink-500 text-pink-500"
                      : activity.entity_type === "employee"
                      ? "border-teal-500 text-teal-500"
                      : ""
                  }`}
                >
                  {activity.entity_type === "order_reminder" && t("reminder")}
                  {activity.entity_type === "payment_request" && t("payment")}
                  {activity.entity_type === "assistance_request" &&
                    t("assistance")}
                  {activity.entity_type === "order" && t("order")}
                  {activity.entity_type === "menu_item" && t("menu")}
                  {activity.entity_type === "store" && t("stores.title")}
                  {activity.entity_type === "attendance" && t("attendance")}
                  {activity.entity_type === "table" && t("tables")}
                  {activity.entity_type === "employee" && t("staff")}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                {formatDistanceToNow(new Date(activity.created_at), {
                  addSuffix: true,
                })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
